// Catching "somebody already suggested that" before it becomes two rows.
//
// Showing the existing candidates helps, but only against duplicates people notice.
// "Deep Creek" and "Deep Creek Lake, MD" read as different strings and geocode a mile
// apart, so proximity is what actually catches them.

export interface PlacePoint {
  id: string
  name: string
  lat: number
  lng: number
}

/** Advisory only — a suggestion this close gets a "same place?" prompt, never a block. */
export const NEAR_DUPLICATE_KM = 25

const EARTH_RADIUS_KM = 6371

const toRad = (deg: number): number => (deg * Math.PI) / 180

export function kmBetween(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const dLat = toRad(b.lat - a.lat)
  const dLng = toRad(b.lng - a.lng)
  const lat1 = toRad(a.lat)
  const lat2 = toRad(b.lat)

  const h =
    Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2)
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)))
}

/** Strip case, punctuation and filler so "Deep Creek, MD" and "deep creek md" match. */
export function normalizePlaceName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * The existing candidate a new suggestion probably duplicates, or null.
 * Same normalized name wins over mere proximity; otherwise the nearest inside the radius.
 */
export function findNearDuplicate(
  candidate: { name: string; lat: number; lng: number },
  existing: readonly PlacePoint[],
  radiusKm: number = NEAR_DUPLICATE_KM,
): PlacePoint | null {
  const wanted = normalizePlaceName(candidate.name)
  if (wanted) {
    const sameName = existing.find((p) => normalizePlaceName(p.name) === wanted)
    if (sameName) return sameName
  }

  let best: PlacePoint | null = null
  let bestKm = Infinity
  for (const p of existing) {
    const km = kmBetween(candidate, p)
    if (km <= radiusKm && km < bestKm) {
      best = p
      bestKm = km
    }
  }
  return best
}

/** How many candidates the intake form shows before the "show all" disclosure. */
export const CANDIDATE_PREVIEW = 6

/**
 * The candidate list as intake renders it: a household's own suggestions first, then the
 * rest, capped unless expanded.
 *
 * Capped because this list is a duplicate check, not a browsable index — /regions is that.
 * Uncapped it put 28 names between the question and the rest of the form on a phone.
 */
export function previewCandidates<T extends { id: string }>(
  candidates: readonly T[],
  ownIds: readonly string[],
  showAll: boolean,
  limit: number = CANDIDATE_PREVIEW,
): { shown: T[]; hidden: number } {
  const own = new Set(ownIds)
  const ordered = [...candidates].sort(
    (a, b) => Number(own.has(b.id)) - Number(own.has(a.id)),
  )
  const shown = showAll ? ordered : ordered.slice(0, limit)
  return { shown, hidden: ordered.length - shown.length }
}
